import { Bot, Loader2, Mic, PhoneOff, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getVoiceStatus, mintVoiceSession, runVoiceTool } from './voiceClient.js';

/**
 * Speech-to-speech assistant over the OpenAI Realtime API (WebRTC).
 *  - The browser captures the mic and streams it directly to OpenAI (low latency).
 *  - OpenAI streams speech back, played via a hidden <audio> element.
 *  - Data tools → POST /ai/realtime/tool (runs with the user's permissions).
 *  - Page navigation + on-screen guidance are handled in the browser.
 * The backend only mints a short-lived ephemeral token; audio never proxies through us.
 */

// Pages the assistant can open by name. `slug` is the route; en/ar/aliases are matched
// against what the user said. Longest match wins ("sales invoices" beats "sales").
interface NavTarget {
  slug: string;
  en: string;
  ar: string;
  aliases?: string[];
}
const NAV_TARGETS: NavTarget[] = [
  { slug: 'dashboard', en: 'dashboard', ar: 'لوحة التحكم', aliases: ['home', 'الرئيسية'] },
  { slug: 'inventory/items', en: 'items', ar: 'الأصناف', aliases: ['products', 'المنتجات', 'البضاعة'] },
  { slug: 'inventory/stock', en: 'stock', ar: 'المخزون', aliases: ['inventory', 'الكميات', 'الرصيد'] },
  { slug: 'inventory/categories', en: 'categories', ar: 'الفئات', aliases: ['التصنيفات'] },
  { slug: 'inventory/brands', en: 'brands', ar: 'العلامات التجارية', aliases: ['الماركات'] },
  { slug: 'inventory/warehouses', en: 'warehouses', ar: 'المخازن', aliases: ['المستودعات'] },
  { slug: 'sales/parties', en: 'customers', ar: 'العملاء', aliases: ['clients', 'الزبائن'] },
  { slug: 'purchasing/suppliers', en: 'suppliers', ar: 'الموردين', aliases: ['vendors', 'الموردون'] },
  { slug: 'sales/invoices', en: 'sales invoices', ar: 'فواتير المبيعات', aliases: ['invoices', 'sales', 'المبيعات', 'الفواتير'] },
  { slug: 'sales/orders', en: 'sales orders', ar: 'طلبات البيع', aliases: ['orders', 'الطلبات'] },
  { slug: 'sales/quotations', en: 'quotations', ar: 'عروض الأسعار', aliases: ['quotes', 'التسعير'] },
  { slug: 'purchasing/bills', en: 'bills', ar: 'فواتير الشراء', aliases: ['purchases', 'المشتريات'] },
  { slug: 'pos/register', en: 'point of sale', ar: 'نقطة البيع', aliases: ['pos', 'register', 'الكاشير', 'الصندوق'] },
  { slug: 'payments', en: 'payments', ar: 'المدفوعات', aliases: ['receipts', 'الدفعات', 'التحصيل'] },
  { slug: 'expenses', en: 'expenses', ar: 'المصروفات', aliases: ['spending', 'النفقات', 'الصرفيات'] },
  { slug: 'accounting/accounts', en: 'chart of accounts', ar: 'دليل الحسابات', aliases: ['accounts', 'الحسابات'] },
  { slug: 'accounting/journals', en: 'journals', ar: 'القيود', aliases: ['journal entries', 'قيود اليومية'] },
  { slug: 'accounting/trial-balance', en: 'trial balance', ar: 'ميزان المراجعة', aliases: ['الميزان'] },
  { slug: 'reports', en: 'reports', ar: 'التقارير', aliases: ['statistics', 'الإحصائيات'] },
  { slug: 'hr/employees', en: 'employees', ar: 'الموظفين', aliases: ['hr', 'staff', 'الموظفون', 'الموارد البشرية'] },
  { slug: 'hr/payroll', en: 'payroll', ar: 'الرواتب', aliases: ['salaries', 'salary', 'الراتب'] },
  { slug: 'crm/leads', en: 'leads', ar: 'العملاء المحتملون', aliases: ['crm', 'الفرص'] },
  { slug: 'mandob', en: 'field reps', ar: 'المندوبين', aliases: ['mandob', 'reps', 'المندوب'] },
  { slug: 'files', en: 'files', ar: 'الملفات', aliases: ['documents', 'المستندات'] },
  { slug: 'settings', en: 'settings', ar: 'الإعدادات', aliases: ['config', 'اعدادات'] },
];

const VOICES = ['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse', 'marin', 'cedar'];
const MAX_SESSION_MS = 5 * 60 * 1000;
const IDLE_HANGUP_MS = 30 * 1000;
const HIGHLIGHT_RGB = 'rgb(91,121,255)'; // brand indigo (rgb, not hex, to satisfy the no-hex lint)

const normMatch = (s: string): string =>
  String(s || '').toLowerCase().replace(/[أإآٱ]/g, 'ا').replace(/ى/g, 'ي').replace(/ة/g, 'ه')
    // Tatweel, then the harakat — as `\p{Mn}` rather than a literal U+064B–U+0652
    // range, which lints as a misleading character class because those marks
    // combine with the character before them in the source.
    .replace(/ـ/g, '').replace(/\p{Mn}/gu, '').trim();

function findElementByText(query: string): HTMLElement | null {
  const nq = normMatch(query);
  if (!nq) return null;
  const sel = 'button, a, [role="button"], [role="menuitem"], [role="tab"], input[type="submit"], input[type="button"], summary, label, [data-voice-target]';
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(sel));
  let best: HTMLElement | null = null;
  let bestLen = Infinity;
  for (const el of nodes) {
    if (!el.offsetParent && el.tagName !== 'SUMMARY') continue;
    const text = normMatch(el.getAttribute('aria-label') || el.getAttribute('title') || (el as HTMLInputElement).value || el.textContent || el.getAttribute('placeholder') || '');
    if (!text) continue;
    if ((text.includes(nq) || nq.includes(text)) && text.length < bestLen) { best = el; bestLen = text.length; }
  }
  return best;
}

function highlightDomElement(el: HTMLElement | null): boolean {
  if (!el) return false;
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const r = el.getBoundingClientRect();
    const ring = document.createElement('div');
    Object.assign(ring.style, {
      position: 'fixed', left: `${r.left - 6}px`, top: `${r.top - 6}px`,
      width: `${r.width + 12}px`, height: `${r.height + 12}px`,
      border: `3px solid ${HIGHLIGHT_RGB}`, borderRadius: '12px', zIndex: '2147483647',
      pointerEvents: 'none', boxShadow: '0 0 0 4px rgba(91,121,255,0.25), 0 0 22px rgba(91,121,255,0.55)',
      transition: 'opacity 0.4s ease',
    });
    document.body.appendChild(ring);
    setTimeout(() => { ring.style.opacity = '0'; }, 3200);
    setTimeout(() => { try { ring.remove(); } catch { /* noop */ } }, 3700);
    return true;
  } catch { return false; }
}

function findFieldByLabel(query: string): HTMLElement | null {
  const nq = normMatch(query);
  if (!nq) return null;
  for (const lab of Array.from(document.querySelectorAll('label'))) {
    if (!normMatch(lab.textContent || '').includes(nq)) continue;
    const forId = lab.getAttribute('for');
    const ctrl = forId ? document.getElementById(forId) : lab.querySelector('input, textarea, select');
    if (ctrl && (ctrl as HTMLElement).offsetParent) return ctrl as HTMLElement;
  }
  let best: HTMLElement | null = null;
  let bestLen = Infinity;
  for (const el of Array.from(document.querySelectorAll<HTMLInputElement>('input, textarea, select'))) {
    if (!el.offsetParent || el.type === 'hidden') continue;
    const text = normMatch(el.getAttribute('aria-label') || el.getAttribute('placeholder') || el.getAttribute('name') || '');
    if (text && (text.includes(nq) || nq.includes(text)) && text.length < bestLen) { best = el; bestLen = text.length; }
  }
  return best;
}

function setNativeValue(el: HTMLElement, value: string): boolean {
  try {
    const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype
      : el.tagName === 'SELECT' ? window.HTMLSelectElement.prototype : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (setter) setter.call(el, value); else (el as HTMLInputElement).value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  } catch { return false; }
}

function listPageActions(): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const el of Array.from(document.querySelectorAll<HTMLElement>('button, a[href], [role="button"], [role="tab"]'))) {
    if (!el.offsetParent) continue;
    const txt = (el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!txt || txt.length > 40) continue;
    const key = txt.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key); out.push(txt);
    if (out.length >= 30) break;
  }
  return out;
}

function matchPage(query: string): NavTarget | null {
  const nq = normMatch(query);
  if (!nq) return null;
  let best: NavTarget | null = null;
  let bestLen = 0;
  for (const tgt of NAV_TARGETS) {
    for (const term of [tgt.en, tgt.ar, ...(tgt.aliases || [])]) {
      const nt = normMatch(term);
      if (nt && (nq.includes(nt) || nt.includes(nq)) && nt.length > bestLen) { best = tgt; bestLen = nt.length; }
    }
  }
  return best;
}

interface Turn { role: 'user' | 'assistant'; text: string; done?: boolean }

export function VoiceCall(): JSX.Element | null {
  const { t, i18n } = useTranslation('ai');
  const lang: 'en' | 'ar' = i18n.language === 'ar' ? 'ar' : 'en';
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'connecting' | 'live' | 'error' | 'ended'>('idle');
  const [error, setError] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [voice, setVoice] = useState<string>(() => { try { return localStorage.getItem('assistantVoice') || ''; } catch { return ''; } });
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let alive = true;
    getVoiceStatus().then((s) => { if (alive) setAvailable(!!s.available); }).catch(() => { if (alive) setAvailable(false); });
    return () => { alive = false; };
  }, []);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const capTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const asstBufRef = useRef('');
  const activeResponseRef = useRef(false);
  const pendingToolsRef = useRef(0);
  const wantResponseRef = useRef(false);
  // De-dupe tool calls: the GA API surfaces a function_call via EITHER
  // response.function_call_arguments.done OR response.output_item.done (we handle
  // both for version compatibility) — guard so the same call_id runs only once.
  const handledCallsRef = useRef<Set<string>>(new Set());

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [turns]);

  const cleanup = useCallback(() => {
    try { if (capTimerRef.current) clearTimeout(capTimerRef.current); } catch { /* noop */ }
    try { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); } catch { /* noop */ }
    try { dcRef.current?.close(); } catch { /* noop */ }
    try { pcRef.current?.getSenders?.().forEach((s) => s.track && s.track.stop()); } catch { /* noop */ }
    try { pcRef.current?.close(); } catch { /* noop */ }
    try { micRef.current?.getTracks().forEach((tr) => tr.stop()); } catch { /* noop */ }
    if (audioRef.current) { try { audioRef.current.srcObject = null; } catch { /* noop */ } }
    pcRef.current = null; dcRef.current = null; micRef.current = null;
    activeResponseRef.current = false; pendingToolsRef.current = 0; wantResponseRef.current = false;
    setSpeaking(false);
  }, []);

  const endCall = useCallback(() => { cleanup(); setStatus('ended'); }, [cleanup]);

  const resetIdle = useCallback(() => {
    try { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); } catch { /* noop */ }
    idleTimerRef.current = setTimeout(() => { endCall(); setOpen(false); }, IDLE_HANGUP_MS);
  }, [endCall]);

  const maybeRespond = useCallback(() => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open' || !wantResponseRef.current) return;
    if (activeResponseRef.current || pendingToolsRef.current > 0) return;
    wantResponseRef.current = false;
    dc.send(JSON.stringify({ type: 'response.create' }));
  }, []);

  const navigateToPage = useCallback((pageQuery: string) => {
    const tgt = matchPage(pageQuery);
    if (!tgt) return { ok: false, error: 'page not recognized', available: NAV_TARGETS.map((x) => (lang === 'ar' ? x.ar : x.en)) };
    const label = lang === 'ar' ? tgt.ar : tgt.en;
    try { navigate(`/${tgt.slug}`); } catch { /* noop */ }
    setOpen(false);
    setTurns((prev) => [...prev, { role: 'assistant', text: `${t('voice.opening')} ${label}…`, done: true }]);
    return { ok: true, opened: label, path: `/${tgt.slug}` };
  }, [navigate, lang, t]);

  const handleToolCall = useCallback(async (callId: string, name: string, argsJson: string) => {
    // Run each tool call once even if both done-events fire for it.
    if (callId && handledCallsRef.current.has(callId)) return;
    if (callId) handledCallsRef.current.add(callId);
    let argsObj: Record<string, unknown> = {};
    try { argsObj = argsJson ? JSON.parse(argsJson) : {}; } catch { argsObj = {}; }
    if (name === 'end_call') {
      setTurns((prev) => [...prev, { role: 'assistant', text: t('voice.ending'), done: true }]);
      setTimeout(() => { endCall(); setOpen(false); }, 1300);
      return;
    }
    pendingToolsRef.current += 1;
    let result: unknown;
    const str = (k: string): string => String(argsObj[k] ?? '');
    if (name === 'open_page') {
      result = navigateToPage(str('page') || str('query') || str('name'));
    } else if (name === 'highlight_element') {
      result = highlightDomElement(findElementByText(str('text'))) ? { ok: true } : { ok: false, error: 'element not found on this page' };
    } else if (name === 'click_element') {
      const el = findElementByText(str('text'));
      if (el) { highlightDomElement(el); try { el.click(); } catch { /* noop */ } result = { ok: true, clicked: str('text') }; }
      else result = { ok: false, error: 'element not found on this page' };
    } else if (name === 'fill_field') {
      const el = findFieldByLabel(str('field') || str('label') || str('name'));
      result = el ? (highlightDomElement(el), setNativeValue(el, str('value')) ? { ok: true } : { ok: false, error: 'could not set value' }) : { ok: false, error: 'field not found' };
    } else if (name === 'page_actions') {
      result = { actions: listPageActions(), current_path: window.location.pathname };
    } else {
      try {
        const data = await runVoiceTool(name, argsObj, lang);
        result = data?.result ?? { error: 'no result' };
      } catch (e) {
        result = { error: (e as Error).message || 'tool failed' };
      }
    }
    const dc = dcRef.current;
    pendingToolsRef.current = Math.max(0, pendingToolsRef.current - 1);
    if (dc && dc.readyState === 'open') {
      dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(result) } }));
      wantResponseRef.current = true;
    }
    maybeRespond();
  }, [lang, maybeRespond, navigateToPage, endCall, t]);

  const onEvent = useCallback((evt: Record<string, unknown>) => {
    const type = String(evt.type || '');
    if (type === 'response.created') { activeResponseRef.current = true; resetIdle(); return; }
    if (type === 'input_audio_buffer.speech_started') { setSpeaking(false); resetIdle(); return; }
    if (type === 'conversation.item.input_audio_transcription.completed') {
      if (evt.transcript) setTurns((prev) => [...prev, { role: 'user', text: String(evt.transcript).trim() }]);
      return;
    }
    if (type === 'response.output_audio_transcript.delta' || type === 'response.audio_transcript.delta') {
      asstBufRef.current += String(evt.delta || '');
      setSpeaking(true); resetIdle();
      setTurns((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant' && !last.done) next[next.length - 1] = { role: 'assistant', text: asstBufRef.current };
        else next.push({ role: 'assistant', text: asstBufRef.current });
        return next;
      });
      return;
    }
    if (type === 'response.output_audio_transcript.done' || type === 'response.audio_transcript.done') {
      asstBufRef.current = '';
      setTurns((prev) => { const next = [...prev]; const last = next[next.length - 1]; if (last && last.role === 'assistant') last.done = true; return next; });
      return;
    }
    if (type === 'response.done') { activeResponseRef.current = false; setSpeaking(false); maybeRespond(); return; }
    if (type === 'response.function_call_arguments.done') { void handleToolCall(String(evt.call_id), String(evt.name), String(evt.arguments ?? '')); return; }
    if (type === 'response.output_item.done') {
      const item = evt.item as { type?: string; call_id?: string; name?: string; arguments?: string } | undefined;
      if (item?.type === 'function_call') void handleToolCall(String(item.call_id), String(item.name), String(item.arguments ?? ''));
      return;
    }
    if (type === 'error') {
      const msg = (evt.error as { message?: string } | undefined)?.message || 'Realtime error';
      if (/active response/i.test(msg)) { activeResponseRef.current = true; return; }
      setError(msg);
    }
  }, [handleToolCall, maybeRespond, resetIdle]);

  const startCall = useCallback(async () => {
    setError(''); setTurns([]); asstBufRef.current = ''; setStatus('connecting');
    activeResponseRef.current = false; pendingToolsRef.current = 0; wantResponseRef.current = false;
    handledCallsRef.current.clear();
    try {
      const sess = await mintVoiceSession({ lang, voice: voice || undefined });
      const ephemeral = sess.client_secret?.value;
      if (!ephemeral) throw new Error('No session token returned');

      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.ontrack = (e) => { if (audioRef.current) audioRef.current.srcObject = e.streams[0] ?? null; };
      pc.onconnectionstatechange = () => {
        if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
          setStatus((s) => (s === 'live' ? 'error' : s));
          if (pc.connectionState === 'failed') setError(t('voice.connectionLost'));
        }
      };
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      micRef.current = mic;
      const track = mic.getAudioTracks()[0];
      if (track) pc.addTrack(track, mic);

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.onmessage = (m) => { try { onEvent(JSON.parse(m.data)); } catch { /* noop */ } };
      dc.onopen = () => {
        setStatus('live'); resetIdle();
        try {
          dc.send(JSON.stringify({
            type: 'response.create',
            response: {
              instructions: lang === 'ar'
                ? 'رحّب بالمستخدم بجملة قصيرة وودّية، واذكر باختصار أنك تقدر تفتح الصفحات وتجيب على أسئلة النظام وترشده على الشاشة. لا تطوّل.'
                : 'Greet the user in one short, friendly sentence and briefly mention you can open pages, answer questions from the system, and guide them on screen. Keep it brief.',
            },
          }));
        } catch { /* noop */ }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const postSdp = (): Promise<Response> => fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST', body: offer.sdp, headers: { Authorization: `Bearer ${ephemeral}`, 'Content-Type': 'application/sdp' },
      });
      let sdpResp = await postSdp();
      for (let attempt = 0; attempt < 2 && sdpResp.status === 429; attempt++) {
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
        sdpResp = await postSdp();
      }
      if (!sdpResp.ok) throw new Error(sdpResp.status === 429 ? t('voice.rateLimited') : `OpenAI SDP exchange failed (${sdpResp.status})`);
      const answer = await sdpResp.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });
      capTimerRef.current = setTimeout(() => { endCall(); }, MAX_SESSION_MS);
    } catch (e) {
      cleanup();
      setStatus('error');
      const msg = (e as Error).message || 'Failed to start voice';
      setError(msg === 'voice_not_configured' ? t('voice.notConfigured') : msg);
    }
  }, [lang, voice, onEvent, cleanup, endCall, t, resetIdle]);

  useEffect(() => () => cleanup(), [cleanup]);

  if (!available) return null;
  const live = status === 'live' || status === 'connecting';

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); if (!live) { setStatus('idle'); setTurns([]); setError(''); } }}
        aria-label={t('voice.talk')}
        title={live ? t('voice.onCall') : t('voice.talk')}
        className={`fixed bottom-6 end-24 z-30 flex h-12 w-12 items-center justify-center rounded-full bg-surface text-brand shadow-lg ring-1 ring-border transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand ${live ? 'animate-pulse ring-2 ring-brand' : ''}`}
      >
        <Mic className="h-5 w-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-bg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-brand p-6 text-center text-brand-contrast">
              <button type="button" onClick={() => setOpen(false)} className="absolute end-4 top-4 rounded-md p-1 hover:bg-black/10" aria-label={t('close')}>
                <X className="h-5 w-5" />
              </button>
              <div className={`mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-2xl bg-black/10 ${speaking ? 'scale-110' : ''} transition-transform`}>
                {status === 'connecting' ? <Loader2 className="h-8 w-8 animate-spin" /> : <Bot className="h-9 w-9" />}
              </div>
              <h3 className="text-lg font-bold">{t('voice.title')}</h3>
              <p className="mt-1 text-xs opacity-90">
                {status === 'idle' && t('voice.hintIdle')}
                {status === 'connecting' && t('voice.connecting')}
                {status === 'live' && (speaking ? t('voice.speaking') : t('voice.listening'))}
                {status === 'ended' && t('voice.ended')}
                {status === 'error' && t('voice.errored')}
              </p>
            </div>

            <div ref={scrollRef} className="min-h-[12rem] flex-1 space-y-2 overflow-y-auto bg-bg-subtle p-4">
              {turns.length === 0 && status !== 'error' && (
                <p className="py-6 text-center text-xs text-fg-subtle">{t('voice.transcriptHint')}</p>
              )}
              {turns.map((turn, i) => (
                <div key={i} className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${turn.role === 'user' ? 'bg-brand text-brand-contrast' : 'border border-border bg-surface text-fg'}`}>
                    {turn.text}
                  </div>
                </div>
              ))}
              {error && (
                <div className="rounded-md border border-danger bg-danger/5 px-3 py-2 text-xs text-danger" role="alert">{error}</div>
              )}
            </div>

            <div className="flex items-center justify-center gap-3 border-t border-border p-4">
              {(status === 'idle' || status === 'ended' || status === 'error') ? (
                <>
                  <button type="button" onClick={() => void startCall()} className="inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-bold text-brand-contrast shadow hover:opacity-90">
                    <Mic className="h-4 w-4" /> {status === 'idle' ? t('voice.start') : t('voice.startAgain')}
                  </button>
                  <select
                    value={voice}
                    onChange={(e) => { setVoice(e.target.value); try { localStorage.setItem('assistantVoice', e.target.value); } catch { /* noop */ } }}
                    title={t('voice.chooseVoice')}
                    className="rounded-xl border border-border bg-bg px-3 py-3 text-sm"
                  >
                    <option value="">{t('voice.defaultVoice')}</option>
                    {VOICES.map((v) => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
                  </select>
                </>
              ) : (
                <button type="button" onClick={endCall} disabled={status === 'connecting'} className="inline-flex items-center gap-2 rounded-xl bg-danger px-6 py-3 text-sm font-bold text-white shadow disabled:opacity-60">
                  <PhoneOff className="h-4 w-4" /> {t('voice.end')}
                </button>
              )}
            </div>
            <p className="pb-3 text-center text-[0.7rem] text-fg-subtle">{t('voice.footer')}</p>
          </div>
        </div>
      )}

      <audio ref={audioRef} autoPlay />
    </>
  );
}
